import { ExternalLink, PenLine } from 'lucide-react'
import { isManualPlatform, type RedeemApp, type RedeemPost, type RedeemPostStatus } from '@/api/client'
import { useRedeemPosts } from '@/lib/queries'
import { PLATFORM_LABEL } from '@/lib/redeem'
import { statusOf } from '@/lib/status'
import { Button, DataTable, EmptyState, PlatformMark, Segmented, StatusPill, When, useQueryParam, type Column } from '@/ui'
import { useOpenDetail } from '../schedule/shared'
import { Section } from './shared'

const STATUSES: RedeemPostStatus[] = ['draft', 'scheduled', 'publishing', 'published', 'failed']

function channelOf(p: RedeemPost): string {
  if (p.platform === 'bluesky') return 'Bluesky'
  if (p.platform === 'reddit') return `r/${p.channel}`
  return p.channel ? `${PLATFORM_LABEL[p.platform]} · ${p.channel}` : PLATFORM_LABEL[p.platform]
}

/** An app's 글 tab: the desk posts of this app, and the lane of posts the
 *  operator puts up by hand. Opening one shows its detail beside the list. */
export default function AppPosts({ app }: { app: RedeemApp }) {
  const [status, setStatus] = useQueryParam('status', 'all')
  const q = useRedeemPosts(app.id, '')
  const open = useOpenDetail()
  const all = q.data?.items ?? []
  const counts = q.data?.counts ?? {}
  const total = Object.values(counts).reduce((a, b) => a + b, 0)

  // Manual channels never take a slot: until marked posted they wait here.
  const byHand = all.filter((p) => isManualPlatform(p.platform) && p.status !== 'published')
  const rest = all.filter((p) => !byHand.includes(p))
  const cur = (STATUSES as string[]).includes(status) ? status : 'all'
  const rows = cur === 'all' ? rest : rest.filter((p) => p.status === cur)

  const columns: Column<RedeemPost>[] = [
    {
      key: 'where',
      header: '채널',
      cell: (p) => (
        <span className="inline-flex items-center gap-1.5">
          <PlatformMark platform={p.platform} />
          <span className="text-table">{channelOf(p)}</span>
        </span>
      ),
    },
    { key: 'title', header: '제목', card: 'title', className: 'max-w-[28rem]', cell: (p) => <span className="line-clamp-2 break-words font-medium">{p.title || '(제목 없음)'}</span> },
    { key: 'status', header: '상태', card: 'meta', cell: (p) => <StatusPill domain="post" status={p.status} scheduledAt={p.scheduled_at} /> },
    {
      key: 'when',
      header: '시각',
      cell: (p) => <When iso={p.status === 'published' ? p.published_at : p.scheduled_at} mode="absolute" className="text-meta" />,
    },
    {
      key: 'link',
      header: '',
      align: 'right',
      cell: (p) =>
        p.external_url ? (
          <a
            href={p.external_url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-meta text-accent hover:underline"
          >
            글 열기 <ExternalLink size={12} aria-hidden />
          </a>
        ) : null,
    },
  ]

  return (
    <div className="space-y-6">
      {byHand.length > 0 && (
        <Section title="직접 올릴 글" hint="뽐뿌·OKKY·X·Threads·Instagram·TikTok은 데스크가 올리지 않는다. 열어서 복사하고 올린 뒤 게시됨으로 표시한다.">
          <ul className="-mx-4 divide-y divide-border border-y border-border">
            {byHand.map((p) => (
              <li key={p.id} className="flex items-center gap-3 px-4 py-2.5">
                <PlatformMark platform={p.platform} />
                <span className="hidden shrink-0 text-meta text-muted-foreground sm:inline">{channelOf(p)}</span>
                <button
                  type="button"
                  onClick={() => open({ kind: 'redeem', id: p.id })}
                  className="min-w-0 flex-1 truncate text-left text-body font-medium hover:underline"
                >
                  {p.title || '(제목 없음)'}
                </button>
                <StatusPill domain="post" status={p.status} />
                <Button size="sm" onClick={() => open({ kind: 'redeem', id: p.id })}>
                  열기
                </Button>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section title="데스크 글" hint="예약과 시각 변경은 일정에서도 한다. 행을 누르면 상세가 열린다.">
        <Segmented<string>
          label="글 상태"
          value={cur}
          onChange={setStatus}
          options={[
            { value: 'all', label: '전체', count: rest.length },
            ...STATUSES.map((s) => ({
              value: s,
              label: statusOf('post', s).label,
              count: rest.filter((p) => p.status === s).length,
            })),
          ]}
        />
        <DataTable
          label={`${app.name} 데스크 글`}
          columns={columns}
          rows={q.data ? rows : undefined}
          loading={q.isPending}
          error={q.error && !q.data ? q.error : undefined}
          onRetry={() => q.refetch()}
          rowKey={(p) => p.id}
          onRowClick={(p) => open({ kind: 'redeem', id: p.id })}
          empty={
            <EmptyState
              icon={PenLine}
              title={total === 0 ? '이 앱의 글이 없다' : '이 상태의 글이 없다'}
              description={total === 0 ? '위의 "데스크 글 쓰기"로 초안을 만든다.' : undefined}
              className="py-8"
            />
          }
        />
      </Section>
    </div>
  )
}
